import { Link } from "react-router-dom";
import CompanyName from "components/Companies/CompanyName";
import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Users,
  Plus,
  Mail,
  X,
  UserMinus,
  UserCheck,
  KeyRound,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RotateCw,
} from "lucide-react";
import { useUsers } from "hooks/crm/useUsers";
import { useInvitations } from "hooks/useInvitations";
import { useFunctions } from "hooks/useFunctions";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal, isPartner } from "utilities/userType";
import { useToast } from "components/Toast";
import ConfirmDialog from "components/ConfirmDialog";
import EmptyState from "components/EmptyState";
import Skeleton from "components/Skeleton";
import UserAvatar from "components/UserAvatar";
import FunctionChip from "components/Functions/FunctionChip";
import FunctionPicker from "components/Functions/FunctionPicker";
import { SearchProvider, useSearch } from "context/SearchContext";
import SearchPanel from "components/SearchPanel";
import { partitionInvitations, buildResendBody } from "utilities/invitationStatus";

function displayNameOf(u = {}) {
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || u.email || "User"
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span className={`badge ${active ? "badge-success" : "badge-muted"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-current" : "bg-muted-foreground"}`} />
      {active ? "Active" : status || "Inactive"}
    </span>
  );
}

function TeamTabInner({ companyId: companyIdProp }) {
  const { filters } = useSearch();
  const { user } = useAuth();
  const effectiveCompanyId = companyIdProp || user?.companyId;
  // Only meaningful when the list spans companies: internal, unscoped.
  const showCompany = !companyIdProp && isInternal(user);
  const internal = isInternal(user);
  const partner = isPartner(user);
  const canCreateCompanyInvite = internal || partner;
  // Mirrors POST /invitations' requireRole("internal", "client:admin",
  // "partner:admin"). Gates Resend on past invitations.
  const canSendInvites = internal || user?.role === "admin";
  const { toast } = useToast();
  const authUser = { id: user.id, accessToken: user.accessToken };

  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const {
    users: members,
    pagination,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useUsers({ filters, page, limit: 25, companyId: companyIdProp });
  const { invitations, loading: invitationsLoading, refetch: refetchInvitations } =
    useInvitations(companyIdProp);

  // Job Functions are a Sound-ops capability — only load/show them for internal
  // viewers, and only on internal team members (client/partner teams stay
  // functions-free). GET /functions is internal-gated (enabled: internal).
  const { functions } = useFunctions({ enabled: internal });
  const fnLabel = useMemo(() => {
    const map = {};
    functions.forEach((f) => (map[f.functionKey] = f.label));
    return (key) => map[key] || key;
  }, [functions]);

  // Optimistic per-user function overlay (uid -> functions[]).
  const [funcOverride, setFuncOverride] = useState({});
  const memberFns = (m) => (funcOverride[m.id] !== undefined ? funcOverride[m.id] : m.functions || []);

  const showFunctions =
    internal && members.some((m) => m.userType === "internal");

  // Reset page when filters change
  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteType, setInviteType] = useState("team");
  const [inviteCompanyType, setInviteCompanyType] = useState("client");
  const [inviteCompanyName, setInviteCompanyName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });

  // Pending = LIVE only (pending and not past expiresAt). Expired + cancelled
  // go to the collapsed "Past invitations" section, one row per email.
  const { live: pendingInvitations, past: pastInvitations } = useMemo(
    () => partitionInvitations(invitations),
    [invitations]
  );
  const [pastOpen, setPastOpen] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const hasActiveFilters = Object.values(filters).some((v) => v !== "" && v !== undefined);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const assignFunctions = async (member, nextFns) => {
    const prev = memberFns(member);
    setFuncOverride((o) => ({ ...o, [member.id]: nextFns }));
    try {
      await makeApiRequest("put", apiRoutes.setUserFunctions(member.id), authUser, effectiveCompanyId, {
        functions: nextFns,
      });
      await refetchMembers();
      setFuncOverride((o) => {
        const n = { ...o };
        delete n[member.id];
        return n;
      });
    } catch (err) {
      setFuncOverride((o) => ({ ...o, [member.id]: prev })); // rollback
      toast({
        status: "destructive",
        title: "Failed to update functions",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      const body = { email: inviteEmail.trim(), role: inviteRole };
      if (canCreateCompanyInvite && inviteType === "company") {
        body.type = "company";
        body.companyType = inviteCompanyType;
        if (inviteCompanyName.trim()) body.companyName = inviteCompanyName.trim();
      }
      await makeApiRequest("post", apiRoutes.createInvitation, authUser, effectiveCompanyId, body);
      toast({ status: "success", title: "Invitation sent" });
      closeInviteModal();
      refetchInvitations();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to send invitation",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // A resend is a fresh POST /invitations with the same body the invite form
  // sends. The API supersedes an expired invite and 409s only on a LIVE one.
  const handleResendInvitation = async (inv) => {
    const body = buildResendBody(inv, { canCreateCompanyInvite });
    if (!body || resendingId) return;
    setResendingId(inv._id);
    try {
      await makeApiRequest("post", apiRoutes.createInvitation, authUser, effectiveCompanyId, body);
      toast({ status: "success", title: "Invitation resent", description: `Sent to ${body.email}.` });
      await refetchInvitations();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to resend invitation",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelInvitation = (id) => {
    setConfirmDialog({
      open: true,
      title: "Cancel invitation",
      description: "Cancel this invitation? The recipient will no longer be able to accept it.",
      confirmLabel: "Cancel Invitation",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest("delete", apiRoutes.cancelInvitation(id), authUser, effectiveCompanyId);
          toast({ status: "success", title: "Invitation cancelled" });
          refetchInvitations();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to cancel invitation",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  const handleChangeRole = async (uid, newRole) => {
    try {
      await makeApiRequest("put", apiRoutes.updateUserProfile(uid), authUser, effectiveCompanyId, {
        role: newRole,
      });
      toast({ status: "success", title: "Role updated" });
      refetchMembers();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to update role",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const handleDeactivate = (uid) => {
    setConfirmDialog({
      open: true,
      title: "Deactivate user",
      description: "Deactivate this user? They will lose access to the platform.",
      confirmLabel: "Deactivate",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest("put", apiRoutes.deactivateUser(uid), authUser, effectiveCompanyId);
          toast({ status: "success", title: "User deactivated" });
          refetchMembers();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to deactivate user",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  // SOUND-248 (B): support could previously only ask a locked-out user to use
  // the public forgot-password form themselves — which does not help the common
  // case, a merchant who cannot get in and has emailed us about it.
  // Confirmed because it sends a real email to a real person.
  const handlePasswordReset = (member) => {
    setConfirmDialog({
      open: true,
      title: "Send password reset",
      description: `Email a password reset link to ${member.email}? The link goes to them — it is never shown here.`,
      confirmLabel: "Send reset email",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest(
            "post",
            apiRoutes.sendUserPasswordReset(member.id),
            authUser,
            effectiveCompanyId
          );
          toast({ status: "success", title: "Password reset sent", description: `Sent to ${member.email}.` });
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to send password reset",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  const handleReactivate = async (uid) => {
    try {
      await makeApiRequest("put", apiRoutes.reactivateUser(uid), authUser, effectiveCompanyId);
      toast({ status: "success", title: "User reactivated" });
      refetchMembers();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to reactivate user",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteEmail("");
    setInviteRole("member");
    setInviteType("team");
    setInviteCompanyType("client");
    setInviteCompanyName("");
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {members.length} of {pagination.total} {pagination.total === 1 ? "member" : "members"}
        </p>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="badge badge-brand ml-1">
                {Object.values(filters).filter((v) => v !== "" && v !== undefined).length}
              </span>
            )}
          </button>
          <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
            <Plus className="h-4 w-4" />
            Invite
          </button>
        </div>
      </div>

      {/* Members */}
      {membersLoading ? (
        <div className="space-y-3">
          <Skeleton.Rect className="h-14 w-full" />
          <Skeleton.Rect className="h-14 w-full" />
          <Skeleton.Rect className="h-14 w-2/3" />
        </div>
      ) : members.length > 0 ? (
        <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {/* SOUND-248 (A): the internal view lists every user on the
                      platform, where "which company is this?" is the first
                      question. Hidden when the tab is already scoped to one
                      company — the answer would be the same on every row. */}
                  {["Member", ...(showCompany ? ["Company"] : []), "Type", "Role", "Status", ...(showFunctions ? ["Functions"] : []), ""].map(
                    (c, i) => (
                      <th
                        key={c || `sp-${i}`}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                      >
                        {c}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((member) => {
                  const isSelf = member.id === user.id;
                  const canManage = !isSelf && (internal || user.role === "admin");
                  const memberIsInternal = member.userType === "internal";
                  const fns = memberFns(member);
                  const isOptimistic = funcOverride[member.id] !== undefined;

                  return (
                    <tr key={member.id} className="align-top transition-colors hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <UserAvatar user={member} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate font-medium text-foreground">
                                {displayNameOf(member)}
                              </span>
                              {isSelf && (
                                <span className="text-xs text-muted-foreground">(you)</span>
                              )}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {member.email || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      {showCompany && (
                        <td className="px-4 py-3">
                          <div className="truncate text-xs text-foreground">
                            {member.companyName ? (
                              <Link
                                to={`/companies/${member.companyId}`}
                                className="hover:underline"
                              >
                                {member.companyName}
                              </Link>
                            ) : (
                              // Reuses the SOUND-630 placeholder so a nameless
                              // company reads the same here as in /companies.
                              <CompanyName company={{ name: member.companyName }} />
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 align-top">
                        <span className="badge badge-muted capitalize">{member.userType || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        {canManage ? (
                          <select
                            className="select h-8 w-24 text-xs"
                            value={member.role}
                            onChange={(e) => handleChangeRole(member.id, e.target.value)}
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className="text-xs capitalize text-foreground">{member.role}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusPill status={member.status} />
                      </td>
                      {/* Functions is internal-only and its chips are
                          arbitrary-length, so it is the column that drives row
                          height. Bounded here so it wraps within itself instead
                          of stretching the whole row. */}
                      {showFunctions && (
                        <td className="px-4 py-3 align-top">
                          {memberIsInternal ? (
                            <div className="flex max-w-[22rem] flex-wrap items-center gap-1.5">
                              {fns.map((key) => (
                                <FunctionChip
                                  key={key}
                                  label={fnLabel(key)}
                                  optimistic={isOptimistic}
                                  onRemove={
                                    canManage
                                      ? () => assignFunctions(member, fns.filter((k) => k !== key))
                                      : undefined
                                  }
                                />
                              ))}
                              {canManage && (
                                <FunctionPicker
                                  functions={functions}
                                  assigned={fns}
                                  onPick={(key) => assignFunctions(member, [...fns, key])}
                                />
                              )}
                              {!canManage && fns.length === 0 && (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      {/* SOUND-248: the two actions sit in a nowrap flex row.
                          Loose in a text-right cell they wrapped onto separate
                          lines and dragged every row's height with them. */}
                      <td className="px-4 py-3 text-right align-top">
                        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                        {canManage && member.email && (
                          <button
                            className="btn btn-ghost h-8 w-8 p-0 text-muted-foreground"
                            onClick={() => handlePasswordReset(member)}
                            title={`Send password reset to ${member.email}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                        )}
                        {canManage &&
                          (member.status === "active" ? (
                            <button
                              className="btn btn-ghost h-8 w-8 p-0 text-destructive"
                              onClick={() => handleDeactivate(member.id)}
                              title="Deactivate user"
                            >
                              <UserMinus className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              className="btn btn-ghost h-8 w-8 p-0 text-success"
                              onClick={() => handleReactivate(member.id)}
                              title="Reactivate user"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={Users}
          heading={hasActiveFilters ? "No members match your filters." : "No team members found."}
        />
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
            className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Pending Invitations */}
      {!invitationsLoading && pendingInvitations.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Pending Invitations · {pendingInvitations.length}
          </h3>
          <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["Email", "Role", "Type", "Sent", "Expires", ""].map((c, i) => (
                    <th
                      key={c || `i-${i}`}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pendingInvitations.map((inv) => (
                  <tr key={inv._id} className="hover:bg-muted/40">
                    <td className="px-4 py-3 text-foreground">{inv.email}</td>
                    <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{inv.role}</td>
                    <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{inv.type}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="btn btn-ghost h-8 w-8 p-0 text-destructive"
                        onClick={() => handleCancelInvitation(inv._id)}
                        title="Cancel invitation"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Past invitations — expired + cancelled, collapsed by default */}
      {!invitationsLoading && pastInvitations.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
            onClick={() => setPastOpen((o) => !o)}
            aria-expanded={pastOpen}
          >
            {pastOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Past invitations · {pastInvitations.length}
          </button>
          {pastOpen && (
            <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {["Email", "Role", "Sent", "Status", ""].map((c, i) => (
                        <th
                          key={c || `p-${i}`}
                          className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {pastInvitations.map((inv) => {
                      const resendable =
                        canSendInvites && buildResendBody(inv, { canCreateCompanyInvite }) !== null;
                      const busy = resendingId === inv._id;
                      return (
                        <tr key={inv._id} className="hover:bg-muted/40">
                          <td className="px-4 py-3 text-foreground">{inv.email}</td>
                          <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{inv.role}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {inv.pastStatus === "cancelled" ? (
                              <span className="badge badge-muted">Cancelled</span>
                            ) : (
                              <span className="badge badge-warning">Expired</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {resendable && (
                              <button
                                type="button"
                                className="btn btn-secondary h-8 whitespace-nowrap px-3 text-xs"
                                onClick={() => handleResendInvitation(inv)}
                                disabled={resendingId !== null}
                                title={`Resend invitation to ${inv.email}`}
                              >
                                <RotateCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                                {busy ? "Sending…" : "Resend"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters drawer */}
      <SearchPanel
        namespace="users"
        title="Filter Members"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />

      <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))} />

      {/* Invite Modal (portal — SOUND-239) */}
      {showInvite &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeInviteModal}>
            <div
              className="surface w-full max-w-md rounded-xl p-6 shadow-lg ring-1 ring-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  {inviteType === "company"
                    ? partner
                      ? "Invite Client"
                      : "Invite New Company"
                    : "Invite Team Member"}
                </h3>

                <label className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">Email</span>
                  <input
                    type="email"
                    className="input"
                    placeholder="name@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </label>

                {canCreateCompanyInvite && (
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Invitation type</span>
                    <select
                      className="select"
                      value={inviteType}
                      onChange={(e) => setInviteType(e.target.value)}
                    >
                      <option value="team">Team (join existing company)</option>
                      <option value="company">{partner ? "New Client" : "New Company"}</option>
                    </select>
                  </label>
                )}

                {canCreateCompanyInvite && inviteType === "company" ? (
                  <>
                    {internal && (
                      <label className="flex flex-col text-sm">
                        <span className="mb-1 font-medium text-foreground">Company type</span>
                        <select
                          className="select"
                          value={inviteCompanyType}
                          onChange={(e) => setInviteCompanyType(e.target.value)}
                        >
                          <option value="client">Client</option>
                          <option value="partner">Partner</option>
                        </select>
                      </label>
                    )}
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 font-medium text-foreground">Company name (optional)</span>
                      <input
                        type="text"
                        className="input"
                        placeholder="e.g. Acme Corp"
                        value={inviteCompanyName}
                        onChange={(e) => setInviteCompanyName(e.target.value)}
                      />
                    </label>
                  </>
                ) : (
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Role</span>
                    <select className="select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                )}

                <div className="flex gap-2 pt-2">
                  <button className="btn btn-ghost flex-1" onClick={closeInviteModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1"
                    onClick={handleInvite}
                    disabled={submitting || !inviteEmail.trim()}
                  >
                    <Mail className="h-4 w-4" />
                    {submitting ? "Sending…" : "Send Invitation"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default function TeamTab({ companyId }) {
  return (
    <SearchProvider namespace="users">
      <TeamTabInner companyId={companyId} />
    </SearchProvider>
  );
}
